/**
 * False-summit timing + pit impulse helpers for The Gauntlet Phase 2.
 * Kinematic fall-and-fade is retired; eliminations hand off to box3d tumblers.
 */

import { pitCenter } from './gauntletCourse';

/** Celebrate green DONE, then flip to red ✗ (seconds). */
export const FS_CELEBRATE_SEC = 0.75;
export const FS_FLIP_SEC = 0.55;

export interface ElimPose {
  x: number;
  y: number;
  z: number;
  scale: number;
  redBlend: number;
  visible: boolean;
}

/**
 * Impulse direction FROM the elimination point INTO the stage pit center,
 * with a modest upward arc and small lateral jitter for pile texture.
 * Writes into `out` (no alloc).
 */
export function pitImpulse(
  stage: 0 | 1 | 2,
  laneZ: number,
  botIndex: number,
  out: { ix: number; iy: number; iz: number },
  from?: { x: number; y: number; z: number },
): { ix: number; iy: number; iz: number } {
  const pit = pitCenter(stage);
  const fx = from?.x ?? pit.x;
  const fy = from?.y ?? pit.y + 2;
  const fz = from?.z ?? laneZ;

  // Direction from elim point toward pit interior (slightly above floor).
  let dx = pit.x - fx;
  let dy = pit.y + 0.5 - fy;
  let dz = pit.z - fz;

  // Small lateral jitter so the heap has texture (not a perfect column).
  const jitterX = ((botIndex % 7) - 3) * 0.05;
  const jitterZ = ((botIndex % 5) - 2) * 0.05;
  dx += jitterX;
  dz += jitterZ;

  // If already over the pit in XZ, drop in with a tiny push for texture.
  const horiz = Math.hypot(dx, dz);
  if (horiz < 0.4) {
    dx = ((botIndex % 3) - 1) * 0.2;
    dz = ((botIndex % 5) - 2) * 0.15 + 0.1;
  }

  // Prefer horizontal travel into the trench over hop. Mild arc clears lips.
  const arc = 0.12 + (botIndex % 3) * 0.02;
  let ix = dx;
  let iy = Math.max(dy * 0.15, -0.45) + arc;
  let iz = dz;

  // High cliff: strong drop + pull toward pit (never hop onto the wall top).
  if (fy > 3.0) {
    const pull = Math.hypot(dx, dz) || 1;
    ix = (dx / pull) * 1.0;
    iy = -0.75;
    iz = (dz / pull) * 1.0;
  }

  // Clamp upward aim so we never fling into orbit.
  if (iy > 0.35) iy = 0.35;

  const len = Math.hypot(ix, iy, iz) || 1;
  out.ix = ix / len;
  out.iy = iy / len;
  out.iz = iz / len;
  return out;
}

/** Brief red flash blend for the last kinematic frame before hide (0..1). */
export function elimRedBlend(t: number): number {
  return Math.min(1, t * 4);
}
